import type { PropsWithChildren, ReactNode } from 'react';
import { Fragment } from 'react';

import { PaywallElement } from '@/paywallElement';
import type { Paywall } from '@/types';

import type { PostContentChildDecoratorComponentType, RewriteImageSrcMethodType } from '../PostContent.types';
import { componentMap } from './constants';
import { PostSubscriberBreak } from './nodes/subscriberBreak';

export function getElement({
  node,
  children,
  key,
  isLive,
  translations,
  homePath,
  loginPath,
  subscribePath,
  upgradePath,
  showSubscriberBreak,
  showPaywallBreak,
  ChildDecoratorComponent,
  paywall,
  isIndexingDisabled,
  rewriteImageSrc,
  isSubscriberAuthenticated,
}: PropsWithChildren<{
  node: any;
  ChildDecoratorComponent?: PostContentChildDecoratorComponentType;
  key: string;
  upgradePath: string;
  isLive?: boolean;
  translations?: Record<string, string>;
  homePath?: string;
  loginPath?: string;
  subscribePath?: string;
  showSubscriberBreak?: boolean;
  showPaywallBreak?: boolean;
  paywall?: Paywall;
  isIndexingDisabled?: boolean;
  rewriteImageSrc?: RewriteImageSrcMethodType;
  isSubscriberAuthenticated?: boolean;
}>): ReactNode {
  let Comp: any;

  const className = `dream-post-content-${node.type}`;

  switch (node.type) {
    case 'heading':
    case 'orderedList':
    case 'blockquoteFigure':
    case 'quote':
    case 'quoteCaption':
    case 'listItem':
    case 'bulletList':
    case 'imageBlock':
    case 'figcaption':
    case 'columns':
    case 'column':
    case 'table':
    case 'tableCell':
    case 'tableHeader':
    case 'tableRow':
    case 'tableOfContents':
    case 'codeBlock':
    case 'paragraph':
    case 'button':
    case 'horizontalRule':
    case 'genericEmbed':
    case 'genericEmbedTitle':
    case 'genericEmbedHost':
    case 'genericEmbedDescription':
    case 'audio':
    case 'fileAttachment':
    case 'rss':
    case 'htmlSnippet':
    case 'advertisementOpportunityLogo':
    case 'automatedAdvertisementPlacement':
    case 'serviceEmbed':
    case 'section':
    case 'emoji':
    case 'footnotesNode':
    case 'footnoteItem':
    case 'poll':
    case 'boost':
    case 'recommendation':
    case 'backlink':
    case 'cashtag':
    case 'podcastEpisodeEmbed':
    case 'productEmbed':
    case 'dynamicContent':
    case 'conditionSet':
      Comp = componentMap[node.type as keyof typeof componentMap];

      return (
        <Comp
          key={key}
          element={node}
          className={className}
          isLive={isLive}
          isIndexingDisabled={isIndexingDisabled}
          rewriteImageSrc={rewriteImageSrc}
          translations={translations}
          homePath={homePath}
          loginPath={loginPath}
          subscribePath={subscribePath}
          isSubscriberAuthenticated={isSubscriberAuthenticated}
        >
          {children}
        </Comp>
      );
    case 'subscriberBreak':
      if (showSubscriberBreak === true) {
        // Using the component explicitly here
        // to provider better type checking

        if (!ChildDecoratorComponent) {
          return (
            <PostSubscriberBreak
              key={key}
              element={node}
              className={className}
              isLive={isLive}
              translations={translations}
              homePath={homePath}
              loginPath={loginPath}
              subscribePath={subscribePath}
              isSubscriberAuthenticated={isSubscriberAuthenticated}
            >
              {children}
            </PostSubscriberBreak>
          );
        }

        return (
          <ChildDecoratorComponent node={node} childNodeType={node.type}>
            {({ modifiedNode }) => (
              <PostSubscriberBreak
                key={key}
                element={modifiedNode}
                className={className}
                isLive={isLive}
                translations={translations}
                homePath={homePath}
                loginPath={loginPath}
                subscribePath={subscribePath}
                isSubscriberAuthenticated={isSubscriberAuthenticated}
              >
                {children}
              </PostSubscriberBreak>
            )}
          </ChildDecoratorComponent>
        );
      }

      return null;
    case 'paywallBreak':
      if (showPaywallBreak) {
        Comp = componentMap[node.type as keyof typeof componentMap];

        if (!ChildDecoratorComponent) {
          return (
            <PaywallElement
              key={key}
              element={node}
              className={className}
              isLive={isLive}
              theme={paywall?.theme}
              upgradePath={upgradePath}
              shouldUseUrl={false}
            >
              {children}
            </PaywallElement>
          );
        }

        return (
          <ChildDecoratorComponent node={node} childNodeType={node.type}>
            {({ modifiedNode }) => (
              <PaywallElement
                key={key}
                element={modifiedNode}
                className={className}
                isLive={isLive}
                theme={paywall?.theme}
                upgradePath={upgradePath}
                shouldUseUrl={false}
              >
                {children}
              </PaywallElement>
            )}
          </ChildDecoratorComponent>
        );
      }

      return null;
    // These cases will always return `SerializeTextLeaf`
    case 'text':
      Comp = componentMap[node.type as keyof typeof componentMap];
      return <Comp key={key} leaf={node} isIndexingDisabled={isIndexingDisabled} />;
    case 'hardBreak':
      return <br key={key} />;
    case 'doc': {
      Comp = componentMap[node.type as keyof typeof componentMap];
      return (
        <Comp key={key} element={node} className={className}>
          {children}
        </Comp>
      );
    }
    case 'footnoteMarker':
      Comp = componentMap[node.type as keyof typeof componentMap];
      return <Comp key={key} element={node} className={className} />;
    default:
      return <Fragment key={key}>{children}</Fragment>;
  }
}
